import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';

export interface ExpensePartTax {
  id: string;
  country_id: string;
  tax_amount: number;
  tax_name: string;
  tax_abbreviation: string;
  explanation_short: string | null;
  explanation_long: string | null;
  display_explanation: string | null;
  paragraph: string | null;
}

export interface ExpensePart {
  id: string;
  amount: number;
  expense_part_sequence_nr: number;
  expense_part_tax: ExpensePartTax | null;
}

export interface ExpenseCashTransactionGroup {
  id: string;
  group_type: string;
  finalized_at: string | null;
  automatic_transaction_creation_allowed: boolean;
  cash_list_id: string;
  cash_list_name: string | null;
  update_date: string | null;
  pliant_transaction_types: string[] | null;
  pliant_transaction_merchant_names: string[] | null;
  pliant_transaction_categories: string[] | null;
}

export interface ExpenseExportSummary {
  id: string;
  inserted_by: string;
  inserted_at: string;
}

export const EXPENSE_CATEGORIES = [
  'ADVERTISING_AND_MARKETING',
  'COMPUTING_AND_SOFTWARE',
  'EDUCATION_AND_TRAINING',
  'ELECTRONICS_AND_IT_EQUIPMENT',
  'ENTERTAINMENT_AND_WELLNESS',
  'FOOD_AND_DRINKS',
  'GIFTS_AND_VOUCHERS',
  'MATERIALS_AND_PACKAGING',
  'OFFICE_SUPPLIES_AND_EQUIPMENT',
  'SERVICES',
  'TRAVEL_AND_ACCOMMODATION',
  'HEALTHCARE',
  'OTHER',
] as const;

export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export interface Expense {
  id: string;
  cash_list_transaction_group_id: string;
  receipt_date: string | null;
  receipt_number: string | null;
  receipt_image_ids: string[];
  receipt_not_available: boolean;
  category: ExpenseCategory | null;
  comment: string | null;
  scheduled_for_export_at: string | null;
  expense_export_id: string | null;
  expense_export: ExpenseExportSummary | null;
  inserted_by: string;
  updated_by: string;
  inserted_at: string;
  updated_at: string;
  merchant_name: string | null;
  expense_parts: ExpensePart[];
  cash_transaction_group: ExpenseCashTransactionGroup | null;
}

export interface ExpenseListMeta {
  current_page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

export interface GetExpensesResponse {
  data: Expense[];
  meta: ExpenseListMeta;
}

export type ExpenseTab = 'open' | 'scheduled' | 'exported';

export interface GetExpensesParams {
  params: {
    page: number;
    pageSize: number;
    tab: ExpenseTab;
    employeeId?: string;
    expenseExportId?: string;
    orderBy?: string;
    orderDirection?: 'asc' | 'desc';
  };
}

export interface CreateExpensePart {
  amount: number;
  expense_part_sequence_nr?: number;
  expense_part_tax_id?: string | null;
}

export interface CreateExpenseBody {
  cash_list_transaction_group_id: string;
  expense_parts: CreateExpensePart[];
  category?: ExpenseCategory | null;
  comment?: string | null;
  receipt_date?: string | null;
  receipt_number?: string | null;
  receipt_image_ids?: string[];
  receipt_not_available?: boolean;
}

export interface CreateExpenseParams {
  body: CreateExpenseBody;
}

export interface CreateExpenseResponse {
  data: Expense;
}

export interface UpdateExpenseBody {
  category?: ExpenseCategory | null;
  comment?: string | null;
  receipt_date?: string | null;
  receipt_number?: string | null;
  receipt_image_ids?: string[];
  receipt_not_available?: boolean;
}

export interface UpdateExpenseParams {
  params: {
    expense_id: string;
  };
  body: UpdateExpenseBody;
}

export interface UpdateExpenseResponse {
  data: Expense;
}

export interface DeleteExpenseParams {
  params: {
    expense_id: string;
    /** Used by the hook to invalidate the per-group cache. */
    cash_list_transaction_group_id: string;
  };
}

export interface ScheduleExpensesForExportParams {
  body: {
    expense_ids: string[];
  };
}

export interface ScheduleExpensesForExportResponse {
  data: Expense[];
}

export interface CreateExpenseExportParams {
  body: {
    expense_ids: string[];
  };
}

export interface CreateExpenseExportResponse {
  data: ExpenseExportSummary;
}

export interface IExpensePhoenixApi extends Api {
  getExpenses(dataIn: GetExpensesParams): Promise<GetExpensesResponse>;
  createExpense(dataIn: CreateExpenseParams): Promise<CreateExpenseResponse>;
  updateExpense(dataIn: UpdateExpenseParams): Promise<UpdateExpenseResponse>;
  deleteExpense(dataIn: DeleteExpenseParams): Promise<void>;
  scheduleExpensesForExport(
    dataIn: ScheduleExpensesForExportParams
  ): Promise<ScheduleExpensesForExportResponse>;
  unscheduleExpensesForExport(
    dataIn: ScheduleExpensesForExportParams
  ): Promise<ScheduleExpensesForExportResponse>;
  createExpenseExport(
    dataIn: CreateExpenseExportParams
  ): Promise<CreateExpenseExportResponse>;
}

export class ExpensePhoenixApi implements IExpensePhoenixApi {
  public getExpenses: IExpensePhoenixApi['getExpenses'];
  public createExpense: IExpensePhoenixApi['createExpense'];
  public updateExpense: IExpensePhoenixApi['updateExpense'];
  public deleteExpense: IExpensePhoenixApi['deleteExpense'];
  public scheduleExpensesForExport: IExpensePhoenixApi['scheduleExpensesForExport'];
  public unscheduleExpensesForExport: IExpensePhoenixApi['unscheduleExpensesForExport'];
  public createExpenseExport: IExpensePhoenixApi['createExpenseExport'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    const baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);

    this.createExpense = modify({
      authContext,
      method: 'POST',
      url: () => `${baseUrl}/expense`,
    });

    this.updateExpense = modify({
      authContext,
      method: 'PATCH',
      url: (params) => `${baseUrl}/expense/${params.expense_id}`,
    });

    this.deleteExpense = modify({
      authContext,
      method: 'DELETE',
      url: (params) => `${baseUrl}/expense/${params.expense_id}`,
    });

    this.scheduleExpensesForExport = modify({
      authContext,
      method: 'POST',
      url: () => `${baseUrl}/expense/schedule-for-export`,
    });

    this.unscheduleExpensesForExport = modify({
      authContext,
      method: 'POST',
      url: () => `${baseUrl}/expense/unschedule-for-export`,
    });

    this.createExpenseExport = modify({
      authContext,
      method: 'POST',
      url: () => `${baseUrl}/expense-export`,
    });

    this.getExpenses = get({
      authContext,
      url: (params) => {
        const query = new URLSearchParams();

        query.append('page', params.page.toString());
        query.append('page_size', params.pageSize.toString());

        let i = 0;

        // Tab-based filters using Flop operators
        if (params.tab === 'open') {
          query.append(`filters[${i}][field]`, 'scheduled_for_export_at');
          query.append(`filters[${i}][op]`, 'empty');
          query.append(`filters[${i}][value]`, 'true');
          i++;
          query.append(`filters[${i}][field]`, 'expense_export_id');
          query.append(`filters[${i}][op]`, 'empty');
          query.append(`filters[${i}][value]`, 'true');
          i++;
        } else if (params.tab === 'scheduled') {
          query.append(`filters[${i}][field]`, 'scheduled_for_export_at');
          query.append(`filters[${i}][op]`, 'not_empty');
          query.append(`filters[${i}][value]`, 'true');
          i++;
          query.append(`filters[${i}][field]`, 'expense_export_id');
          query.append(`filters[${i}][op]`, 'empty');
          query.append(`filters[${i}][value]`, 'true');
          i++;
        } else if (params.tab === 'exported') {
          query.append(`filters[${i}][field]`, 'expense_export_id');
          query.append(`filters[${i}][op]`, 'not_empty');
          query.append(`filters[${i}][value]`, 'true');
          i++;
        }

        if (params.employeeId) {
          query.append(`filters[${i}][field]`, 'inserted_by');
          query.append(`filters[${i}][op]`, '==');
          query.append(`filters[${i}][value]`, params.employeeId);
          i++;
        }

        if (params.expenseExportId) {
          query.append(`filters[${i}][field]`, 'expense_export_id');
          query.append(`filters[${i}][op]`, '==');
          query.append(`filters[${i}][value]`, params.expenseExportId);
          i++;
        }

        if (params.orderBy) {
          query.append('order_by[]', params.orderBy);
          query.append('order_directions[]', params.orderDirection ?? 'desc');
        }

        return `${baseUrl}/expense?${query.toString()}`;
      },
    });
  }
}
